import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Popover,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { RiArrowDownSLine, RiBarChart2Line, RiStarFill, RiStarLine } from "@remixicon/react";
import {
  getBookingById,
  getChildNames,
  getLessonCounts,
  getRatingsForLessons,
  getRecentBookings,
  getStudentsAndTeachers,
  LESSON_WINDOW_SIZE,
} from "../../services/dataService";
import { useAuth } from "../../contexts/AuthContext";
import { LESSON_TABS, tabByLabel, matchesTab } from "./lessonTabs";
import { dayLabel, timeLabel, whenLabel } from "../../utils/queuePresentation";
import { childKey, childPath, learnerLabel } from "../../utils/lessonPresentation";
import { sessionStatusChip } from "../../utils/studentDetail";
import {
  countTiles,
  dateRangeLabel,
  filterLessons,
  lessonRatings,
  lessonShortId,
  lessonsShowingLabel,
  personOptions,
  subjectOptions,
  subjectText,
} from "../../utils/lessonDetail";
import { pageSlice, ratingText } from "../../utils/tutorPresentation";
import { toDate } from "../../utils/ticketPresentation";
import { srOnly, tokens } from "../../utils/figmaTokens";
import { SectionCard, TableCard } from "../../components/kit/SectionCard";
import StatStrip from "../../components/kit/StatStrip";
import StatusChip from "../../components/kit/StatusChip";
import { StateAction } from "../../components/kit/States";
import { EyeButton, PersonCell, SkeletonRows, SortHead, TableMessageRow, TwoLineCell } from "../../components/kit/TableParts";
import Pager from "../../components/kit/Pager";
import SearchField from "../../components/kit/SearchField";
import SmallButton from "../../components/kit/SmallButton";
import ToolbarMenu, { buttonSx as toolbarButtonSx } from "../../components/kit/ToolbarMenu";
import { LessonMenu } from "./LessonActions";

// The Lessons list (047 step 6, D90–D98), drawn from Figma's Sessions list (11431:74050): the
// overview card's three counts, then the table card with its search and six menus, the loaded
// window paged 25 at a time, and "Load 500 older lessons". The Status menu picks the query, as
// the tabs did (lessonTabs.js); the other menus and the search filter the loaded lessons. A row
// or its eye opens the lesson page; ⋯ opens the staff doors.

const COLUMNS = 8;
const PAGE_SIZES = [25, 50, 100];
const STATUS_OPTIONS = LESSON_TABS.map(({ label }) => ({ key: label, label }));
const DURATION_OPTIONS = [
  { key: "all", label: "All" },
  { key: "25", label: "25 min" },
  { key: "55", label: "55 min" },
];
const NO_FILTERS = { subject: "all", duration: "all", teacherId: "all", studentId: "all", from: "", to: "" };

// id -> { name, phone }: what the table, its search and its menus read.
const toPersonMap = (people) =>
  Object.fromEntries(
    people.map((p) => [p.id, { name: p.name || p.fullName || p.displayName || p.email || p.id, phone: p.phone || p.phoneNumber || "" }])
  );

// The Date menu (D95): two native date fields, on the lesson's start as the column shows it.
const DateMenu = ({ from, to, onChange }) => {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Button
        onClick={(e) => setAnchor(e.currentTarget)}
        disableFocusRipple
        sx={toolbarButtonSx}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
      >
        Date: {dateRangeLabel(from, to)}
        <RiArrowDownSLine size={24} />
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { role: "dialog", "aria-label": "Date range" } }}
      >
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2, width: 260 }}>
          <TextField
            size="small"
            type="date"
            label="From"
            value={from}
            onChange={(e) => onChange({ from: e.target.value, to })}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: to || undefined } }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={to}
            onChange={(e) => onChange({ from, to: e.target.value })}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: from || undefined } }}
          />
          <SmallButton
            tone="outline"
            disabled={!from && !to}
            onClick={() => {
              onChange({ from: "", to: "" });
              setAnchor(null);
            }}
          >
            Clear dates
          </SmallButton>
        </Box>
      </Popover>
    </>
  );
};

// The RATING cell (D96): the learner's rating of the tutor, "Not rated", or nothing while it is
// read or when the read failed.
const RatingCell = ({ value }) => {
  if (value === undefined) return null;
  if (value === null) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          px: "8px",
          py: "2px",
          borderRadius: "8px",
          border: `1px solid ${tokens.border.strong}`,
          color: tokens.text.primary,
          fontSize: 12,
          lineHeight: "16px",
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        <RiStarLine size={14} aria-hidden />
        Not rated
      </Box>
    );
  }
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "8px", fontWeight: 500, whiteSpace: "nowrap" }}>
      <RiStarFill size={14} color={tokens.star} aria-hidden />
      {ratingText(value)}
    </Box>
  );
};

const Lessons = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("All");
  const [bookings, setBookings] = useState([]);
  const [studentById, setStudentById] = useState({});
  const [teacherById, setTeacherById] = useState({});
  const [childNames, setChildNames] = useState({});
  const [counts, setCounts] = useState({});
  const [ratingOf, setRatingOf] = useState({}); // lessonId -> the learner's rating, or null
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  // True when the status query fell back to an unfiltered window (its index is missing or still
  // building), so an empty status means "none in the loaded window", not "none at all".
  const [windowIsGlobal, setWindowIsGlobal] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(NO_FILTERS);
  const [sortKey, setSortKey] = useState("startTimeUTC");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const loadToken = useRef(0);
  const askedRatings = useRef(new Set());

  const notify = (message, severity = "success") => setSnack({ open: true, message, severity });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // The people and the counts are read once per visit, not on every status change.
  useEffect(() => {
    getStudentsAndTeachers().then((people) => {
      setStudentById(toPersonMap(people.students));
      setTeacherById(toPersonMap(people.teachers));
    });
    getLessonCounts().then(setCounts);
  }, []);

  // D13: one read per distinct child the rows name, nothing for an adult's lesson.
  const loadChildNames = (rows) => {
    const pairs = rows.map(childPath).filter(Boolean);
    if (pairs.length) getChildNames(pairs).then((names) => setChildNames((prev) => ({ ...prev, ...names })));
  };

  // Active/Completed/Cancelled filter on the server; Reported orders by the sparse reportedAt.
  const queryFor = (label, more = null) => {
    const t = tabByLabel(label);
    return {
      ...(t.status ? { status: t.status } : {}),
      ...(t.orderField ? { orderField: t.orderField } : {}),
      pageSize: LESSON_WINDOW_SIZE,
      ...(more ? { cursor: more } : {}),
    };
  };

  // The newest read wins, so a slow read for the last status cannot land over this one.
  const fetchBookings = useCallback(async () => {
    const token = ++loadToken.current;
    setLoading(true);
    setLoadError("");
    try {
      const first = await getRecentBookings(queryFor(tab));
      if (token !== loadToken.current) return;
      setBookings(first.rows);
      setCursor(first.cursor);
      setHasMore(first.hasMore);
      setWindowIsGlobal(Boolean(tabByLabel(tab).status) && !first.serverFiltered);
      setPage(0);
      loadChildNames(first.rows);
    } catch (error) {
      if (token !== loadToken.current) return;
      console.error("Error loading lessons:", error);
      setBookings([]);
      setCursor(null);
      setHasMore(false);
      setLoadError("Nothing has been changed.");
    } finally {
      if (token === loadToken.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Reaches further back, adding to the loaded window; the search and menus then cover it.
  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    // A Status change while this reads makes it stale: its rows and cursor belong to the old query.
    const token = loadToken.current;
    setLoadingMore(true);
    try {
      const next = await getRecentBookings(queryFor(tab, cursor));
      if (token !== loadToken.current) return;
      setBookings((prev) => {
        const seen = new Set(prev.map((b) => b.id));
        return [...prev, ...next.rows.filter((b) => !seen.has(b.id))];
      });
      loadChildNames(next.rows);
      setCursor(next.cursor);
      setHasMore(next.hasMore);
    } catch (error) {
      if (token !== loadToken.current) return;
      console.error("Error loading older lessons:", error);
      notify("Could not load older lessons. Nothing has been changed.", "error");
    } finally {
      setLoadingMore(false);
    }
  };

  const learnerOf = useCallback(
    (b) => learnerLabel(b, { childName: childNames[childKey(b)], parentName: studentById[b.studentId]?.name }),
    [childNames, studentById]
  );

  // Belt and braces with the query: a window read for one status never shows another's lesson.
  const tabRows = useMemo(() => bookings.filter((b) => matchesTab(b, tab)), [bookings, tab]);
  const pick = (value) => (value === "all" ? "" : value);
  const filtered = useMemo(() => {
    const rows = filterLessons(
      tabRows,
      {
        search,
        subject: pick(filters.subject),
        duration: pick(filters.duration),
        teacherId: pick(filters.teacherId),
        studentId: pick(filters.studentId),
        from: filters.from,
        to: filters.to,
      },
      { studentById, teacherById, childNames }
    );
    const valueOf = (b) => {
      if (sortKey === "startTimeUTC") return toDate(b.startTimeUTC)?.getTime() || 0;
      if (sortKey === "student") return learnerOf(b).toLowerCase();
      if (sortKey === "teacher") return (teacherById[b.teacherId]?.name || "").toLowerCase();
      if (sortKey === "status") return sessionStatusChip(b).label.toLowerCase();
      return String(b[sortKey] || "").toLowerCase();
    };
    const sign = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      return av < bv ? -sign : av > bv ? sign : 0;
    });
  }, [tabRows, search, filters, studentById, teacherById, childNames, sortKey, sortDir, learnerOf]);

  // Anything that changes what matches starts again on page 1.
  useEffect(() => {
    setPage(0);
  }, [search, filters, sortKey, sortDir, tab, pageSize]);

  const slice = pageSlice(filtered, page, pageSize);

  // The RATING column: one read per page of rows, for the lessons not asked about yet.
  // ponytail: a failed read leaves those cells empty until the page is shown again; add a Retry
  // if it proves flaky.
  const pageIds = slice.rows.map((b) => b.id).join(",");
  useEffect(() => {
    const missing = (pageIds ? pageIds.split(",") : []).filter((id) => !askedRatings.current.has(id));
    if (!missing.length) return;
    missing.forEach((id) => askedRatings.current.add(id));
    getRatingsForLessons(missing)
      .then((rows) =>
        setRatingOf((prev) => {
          const next = { ...prev };
          missing.forEach((id) => {
            next[id] = lessonRatings(rows.filter((r) => r.lessonId === id)).learner?.rating ?? null;
          });
          return next;
        })
      )
      .catch((error) => {
        console.error("Error loading ratings:", error);
        missing.forEach((id) => askedRatings.current.delete(id));
      });
  }, [pageIds]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "startTimeUTC" ? "desc" : "asc");
    }
  };

  // After a door on a row: say what happened and read that lesson again.
  const afterAction = async (id, message, severity) => {
    if (message) notify(message, severity);
    if (severity === "error") return;
    const fresh = await getBookingById(id);
    if (fresh) setBookings((prev) => prev.map((b) => (b.id === fresh.id ? fresh : b)));
  };

  const filtersOn = Boolean(search.trim()) || Object.keys(NO_FILTERS).some((k) => filters[k] !== NO_FILTERS[k]);
  const clearFilters = () => {
    setSearch("");
    setFilters(NO_FILTERS);
  };
  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const failed = Boolean(loadError) && !loading;
  const subjects = useMemo(() => subjectOptions(bookings, filters.subject), [bookings, filters.subject]);
  const tutors = useMemo(() => personOptions(teacherById), [teacherById]);
  const learners = useMemo(() => personOptions(studentById), [studentById]);

  const statusWordLower = tab === "All" ? "" : `${tab.toLowerCase()} `;
  const empty = filtersOn
    ? {
        title: "No lessons match.",
        message: hasMore ? `Only the loaded lessons are searched. Load ${LESSON_WINDOW_SIZE} older lessons to look further back.` : "",
      }
    : windowIsGlobal && hasMore
      ? {
          title: `No ${statusWordLower}lessons in the ${LESSON_WINDOW_SIZE} most recent.`,
          message: `Load ${LESSON_WINDOW_SIZE} older lessons to look further back.`,
        }
      : { title: `No ${statusWordLower}lessons yet.`, message: "" };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {/* Figma draws no page head, so the page's h1 is for screen readers only (P59). */}
      <Typography component="h1" sx={srOnly}>
        Lessons
      </Typography>

      <SectionCard title="Lessons overview" Icon={RiBarChart2Line} sx={{ bgcolor: tokens.surface.default }}>
        <StatStrip items={countTiles(counts)} cellGap="8px" />
      </SectionCard>

      <TableCard
        title="Lessons"
        subtitle="Every booking, with its status and tutor"
        toolbar={
          <>
            <SearchField value={search} onChange={setSearch} placeholder="Lesson id, learner, tutor, subject…" />
            <ToolbarMenu id="lessons-status" label="Status" value={tab} options={STATUS_OPTIONS} onChange={setTab} />
            <ToolbarMenu id="lessons-subject" label="Subject" value={filters.subject} options={subjects} onChange={setFilter("subject")} />
            <ToolbarMenu id="lessons-duration" label="Duration" value={filters.duration} options={DURATION_OPTIONS} onChange={setFilter("duration")} />
            <ToolbarMenu id="lessons-tutor" label="Tutor" value={filters.teacherId} options={tutors} onChange={setFilter("teacherId")} />
            <ToolbarMenu id="lessons-learner" label="Learner" value={filters.studentId} options={learners} onChange={setFilter("studentId")} />
            <DateMenu from={filters.from} to={filters.to} onChange={(range) => setFilters((f) => ({ ...f, ...range }))} />
          </>
        }
        footer={
          <Box>
            <Pager
              label={loading || failed ? "" : lessonsShowingLabel(slice.page, slice.rows.length, pageSize, filtered.length, tabRows.length)}
              page={loading || failed ? 0 : slice.page}
              hasMore={!loading && !failed && slice.hasMore}
              onPage={setPage}
              pageSize={pageSize}
              pageSizes={PAGE_SIZES}
              onPageSize={setPageSize}
            />
            {!loading && !failed && hasMore && (
              <Box sx={{ display: "flex", justifyContent: "center", px: "16px", pb: "12px" }}>
                <SmallButton tone="outline" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading older lessons…" : `Load ${LESSON_WINDOW_SIZE} older lessons`}
                </SmallButton>
              </Box>
            )}
          </Box>
        }
      >
        <TableContainer>
          <Table aria-busy={loading}>
            <TableHead>
              <TableRow>
                <TableCell>Lesson</TableCell>
                <SortHead label="Learner" active={sortKey === "student"} direction={sortDir} onSort={() => toggleSort("student")} />
                <SortHead label="Subject" active={sortKey === "subject"} direction={sortDir} onSort={() => toggleSort("subject")} />
                <SortHead label="Tutor" active={sortKey === "teacher"} direction={sortDir} onSort={() => toggleSort("teacher")} />
                <SortHead label="Status" active={sortKey === "status"} direction={sortDir} onSort={() => toggleSort("status")} />
                <TableCell>Rating</TableCell>
                <SortHead label="Date & time" active={sortKey === "startTimeUTC"} direction={sortDir} onSort={() => toggleSort("startTimeUTC")} />
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <SkeletonRows columns={COLUMNS} />
              ) : failed || slice.rows.length === 0 ? (
                <TableMessageRow
                  colSpan={COLUMNS}
                  error={failed ? "Could not load lessons" : ""}
                  errorMessage={loadError}
                  onRetry={fetchBookings}
                  emptyText={empty.title}
                  emptyMessage={empty.message}
                  emptyActions={
                    filtersOn && (
                      <StateAction outlined onClick={clearFilters}>
                        Clear filters
                      </StateAction>
                    )
                  }
                />
              ) : (
                slice.rows.map((b) => {
                  const learner = learnerOf(b);
                  const tutor = teacherById[b.teacherId]?.name;
                  const subject = subjectText(b.subject);
                  const label = [subject || "Lesson", learner, whenLabel(b.startTimeUTC)].join(" · ");
                  return (
                    <TableRow key={b.id} hover onClick={() => navigate(`/lessons/${b.id}`)} sx={{ cursor: "pointer" }}>
                      <TableCell sx={{ color: tokens.text.tertiary, whiteSpace: "nowrap" }}>{lessonShortId(b.id)}</TableCell>
                      <TableCell>
                        <PersonCell
                          name={learner}
                          avatarName={childNames[childKey(b)] || studentById[b.studentId]?.name || learner}
                          colorKey={childKey(b) || b.studentId}
                        />
                      </TableCell>
                      <TableCell>
                        {subject ? (
                          <Chip label={subject} color="info" />
                        ) : (
                          <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <PersonCell name={tutor || "—"} avatarName={tutor || "?"} colorKey={b.teacherId} />
                      </TableCell>
                      <TableCell>
                        <StatusChip {...sessionStatusChip(b)} />
                      </TableCell>
                      <TableCell>
                        <RatingCell value={ratingOf[b.id]} />
                      </TableCell>
                      <TableCell>
                        <TwoLineCell
                          top={dayLabel(b.startTimeUTC)}
                          bottom={`${timeLabel(b.startTimeUTC)} · ${b.duration ? `${b.duration} min` : "—"}`}
                        />
                      </TableCell>
                      {/* The row opens the lesson; its buttons and their dialogs must not open it too. */}
                      <TableCell onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: "nowrap" }}>
                        <EyeButton to={`/lessons/${b.id}`} label={`Open lesson ${lessonShortId(b.id)}`} />
                        <LessonMenu
                          booking={b}
                          label={label}
                          personName={studentById[b.studentId]?.name || b.studentId}
                          actingUser={user}
                          onDone={(message, severity) => afterAction(b.id, message, severity)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={closeSnack} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} onClose={closeSnack}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// An old link (/lessons?lesson=<id>) opens the lesson page (D92) in place of this entry, before the
// list reads anything.
const LessonsRoute = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("lesson");
  return id ? <Navigate to={`/lessons/${id}`} replace /> : <Lessons />;
};

export default LessonsRoute;
